<template>
  <Dialog title="Edit Experiment" :show="isOpen" :onClose="onClose">
    <el-form
      :model="item"
      label-width="140px"
      label-position="left"
      size="small"
    >
      <el-form-item label="Name" required>
        <el-input v-model="item.name" :disabled="!!item.id" />
      </el-form-item>
      <el-form-item label="Description">
        <el-input v-model="item.description" type="textarea" />
      </el-form-item>
      <el-form-item label="Period (days)">
        <el-input v-model="item.period" type="number" :min="0" />
      </el-form-item>
      <el-form-item label="Active">
        <el-radio-group v-model="item.is_active">
          <el-radio :label="true" disabled>Yes</el-radio>
          <el-radio :label="false" disabled>No</el-radio>
        </el-radio-group>
        <div>Toggle via Deploy / End buttons on the main table.</div>
      </el-form-item>
      <el-form-item label="Type" required>
        <el-radio-group v-model="item.experiment_type">
          <el-radio label="ab">A/B</el-radio>
          <el-radio label="variants">Variants</el-radio>
          <el-radio label="feature_toggle">Feature toggle</el-radio>
        </el-radio-group>
      </el-form-item>
      <div v-if="isABTest">
        <el-form-item label="Traffic (0-1)" required>
          <el-input
            v-model="item.traffic"
            type="number"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </el-form-item>
      </div>
      <div v-else-if="isVariants">
        <div class="title">
          <h3>Variants</h3>
          <!-- <el-button type="primary" size="mini" @click="addVariant" icon="el-icon-plus" round>Add</el-button> -->
        </div>
        <el-form-item
          :label="`Variant ${index + 1}`"
          v-for="(v, index) in item.variants"
          :key="index"
        >
          <el-form inline size="mini" label-position="top">
            <el-form-item label="Name">
              <el-input v-model="v.value" :readonly="!v.isNew" />
            </el-form-item>
            <el-form-item label="Traffic (0-1)" style="width: 160px">
              <el-input
                v-model="v.traffic"
                type="number"
                :min="0"
                :max="1"
                :step="0.1"
              />
            </el-form-item>
            <el-form-item label="Remove" v-if="v.isNew">
              <el-link type="danger" @click="removeVariant(index)"
                >Remove</el-link
              >
            </el-form-item>
          </el-form>
        </el-form-item>
        <el-form-item :label="`Variant ${item.variants.length + 1}`">
          <el-form inline size="mini" label-position="top">
            <el-form-item label="Name">
              <el-input v-model="variant.value" />
            </el-form-item>
            <el-form-item label="Traffic (0-1)" style="width: 160px">
              <el-input
                v-model="variant.traffic"
                type="number"
                :min="0"
                :max="1"
                :step="0.1"
              />
            </el-form-item>
            <el-form-item label="Add">
              <el-button type="primary" @click="addVariant">Add</el-button>
            </el-form-item>
          </el-form>
        </el-form-item>
      </div>
      <div v-else-if="isFeatureToggle">
        <el-form-item label="Feature on/off" required>
          <el-switch v-model="ftValue" active-text="On" inactive-text="Off" />
        </el-form-item>
      </div>
      <el-form-item label="New users only?" v-if="!isFeatureToggle">
        <el-switch
          v-model="item.is_for_new_users"
          active-text="Yes"
          inactive-text="No"
        />
      </el-form-item>
      <el-form-item label="Segment" v-if="!isFeatureToggle">
        <el-select
          v-model="item.cohort_id"
          filterable
          clearable
          :loading="loading.cohorts"
          placeholder="Filter by Cohort name..."
        >
          <el-option
            v-for="cohort in cohorts"
            :key="cohort.id"
            :value="cohort.id"
            :label="cohort.name"
          />
        </el-select>
      </el-form-item>

      <el-form-item>
        <el-button type="primary" @click="save">{{
          item.id ? "Save" : "Create"
        }}</el-button>
        <el-button @click="onClose()">Cancel</el-button>
      </el-form-item>
    </el-form>
  </Dialog>
</template>

<script>
import { isEmpty, omit } from "lodash";
import { mapGetters, mapActions } from "vuex";
import Dialog from "@/components/Dialog";

export default {
  data() {
    return { ftValue: true, variant: { value: "", traffic: 0.5, isNew: true } };
  },
  props: {
    item: Object,
    isOpen: Boolean,
    onClose: Function,
  },
  components: {
    Dialog,
  },
  computed: {
    ...mapGetters(["loading", "cohorts"]),
    isFeatureToggle() {
      return this.item?.experiment_type === "feature_toggle";
    },
    isABTest() {
      return this.item?.experiment_type === "ab";
    },
    isVariants() {
      return this.item?.experiment_type === "variants";
    },
  },
  methods: {
    ...mapActions(["getExperiments"]),
    addVariant() {
      this.item.variants.push(this.variant);
      this.variant = { value: "", traffic: 0.5, isNew: true };
    },
    removeVariant(index) {
      this.item.variants.splice(index, 1);
    },
    async save() {
      try {
        const { id, name } = this.item;
        let variants = this.item.variants.map((v) => omit(v, "isNew"));

        if (!name) {
          return this.$message.error("Please fill experiment name");
        }

        if (!this.isVariants) {
          this.item = omit(this.item, "variants");
          variants = undefined;
        }
        if (this.isVariants) {
          if (this.variant.value && this.variant.traffic) {
            variants.push(this.variant);
          }
          if (isEmpty(variants)) {
            return this.$message.error("There must be at least one variant");
          }

          if (!this.item.variants.every((v) => v.value && v.traffic >= 0))
            return this.$message.error("Please fill info for all variants");

          const sum = this.item.variants.reduce(
            (acc, v) => acc + parseFloat(v.traffic),
            0
          );
          if (sum > 1 || sum < 0)
            return this.$message.error(
              "The sum of variants' traffic has to be between 0 and 1"
            );

          this.item.traffic = sum;
        }
        if (this.isFeatureToggle) {
          this.item.traffic = Number(this.ftValue);
          this.item.cohort_id = null;
          this.item.is_for_new_users = false;
        }

        await this.$api({
          url: `/product/experiments/${id || ""}`,
          method: id ? "put" : "post",
          data: { ...this.item, variants },
        });

        this.$message.success(`${id ? "Saved" : "Created"} successfully`);
        this.onClose();
      } catch (e) {
        this.$notify.error({
          title: "Failed saving Experiment",
          message: e.message,
        });
      } finally {
        this.getExperiments();
      }
    },
  },
  watch: {
    item({ experiment_type, traffic } = {}) {
      if (experiment_type === "feature_toggle") {
        this.ftValue = !!traffic;
      }
    },
  },
};
</script>

<style scoped>
.title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
